import { ReactNode, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { toast } from "@/hooks/use-toast";
import type { ConciergeSite } from "@/lib/concierge/api";
import { useMyConsent, useGiveConsent, isActive } from "@/lib/concierge/consent";

export function ConsentGate({
  site,
  coachHandle,
  children,
}: {
  site: ConciergeSite & { coach_user_id?: string | null };
  coachHandle?: string | null;
  children: ReactNode;
}) {
  const { data: consent, isLoading } = useMyConsent(site.id);
  const give = useGiveConsent();
  const navigate = useNavigate();
  const [pending, setPending] = useState(false);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="h-5 w-5 animate-spin opacity-60" />
      </div>
    );
  }

  if (isActive(consent)) return <>{children}</>;

  const coachLabel = coachHandle?.trim() || "your coach";
  const withdrawn = !!consent?.withdrawn_at;

  return (
    <div className="max-w-2xl mx-auto px-6 py-16">
      <Card>
        <CardContent className="p-8 space-y-5">
          <div className="space-y-2">
            <h1 className="text-2xl font-heading font-semibold tracking-tight">
              A quick heads-up before you start
            </h1>
            <p className="text-sm text-muted-foreground">
              This space is different from the anonymous Pulse surveys.
            </p>
          </div>
          <div className="space-y-3 text-sm leading-relaxed">
            <p>
              Your coach on this space is <span className="font-medium">{coachLabel}</span>.
              They can see when you visit, which resources you open, and when you talk to
              your advisor.
            </p>
            <p>
              They <span className="font-medium">cannot</span> see the words of your
              conversations with your advisor. Voice sessions stay private in this phase.
            </p>
            <p>
              You can withdraw at any time from the bottom of this page. If you do, new
              activity stops being recorded.
            </p>
          </div>
          {withdrawn && (
            <p className="text-xs text-muted-foreground border border-border rounded-md px-3 py-2">
              You previously withdrew consent. Continuing will re-enable activity recording.
            </p>
          )}
          <div className="flex gap-2 pt-2">
            <Button
              disabled={pending}
              onClick={async () => {
                setPending(true);
                try {
                  await give.mutateAsync({
                    site_id: site.id,
                    coach_user_id: site.coach_user_id ?? null,
                  });
                } catch (e: any) {
                  toast({
                    title: "Couldn't save consent",
                    description: e.message,
                    variant: "destructive",
                  });
                } finally {
                  setPending(false);
                }
              }}
            >
              {pending ? <Loader2 className="h-4 w-4 animate-spin" /> : "I understand — continue"}
            </Button>
            <Button variant="ghost" onClick={() => navigate("/app/employee")}>
              Not now
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

export default ConsentGate;
